import { AppType } from "client/Api/Apps/common";
import {
	ReactElement,
	useCallback
} from "react";
import { useRenderManager } from "../../useRenderManager";
import { OneByOneTileContent } from "../OneByOneTileContent";
import { TwoByTwoTileContent } from "../TwoByTwoTileContent";
import { VilansKickLiveTileContent } from "./VilansKickLiveTileContent";
import { navigateToVilansKickApp } from "client_legacy/Apps/appNavigation";
import { useRegisterTileUsage } from "../useRegisterTileUsage";
import { TileRenderState } from "../../RenderState/common";
import { useBaseNavigation } from "../useInvokeNavigation";
import { TileContextProvider } from "../../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type VilansKickTileContentProps = {
	renderState: TileRenderState<AppType.VilansKick>;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const VilansKickTileContent = (props: VilansKickTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu
	} = props;

	const [navigationElement, navigateHandler] = useVilansKickTileNavigation(renderState, onEdit);

	const {
		dimension,
		data: {
			settings,
			isConfiguredCorrectly
		}
	} = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<VilansKickLiveTileContent
				isConfiguredCorrectly={isConfiguredCorrectly}
				connectionId={settings.connection.id}
				destinationWindow={settings.destinationWindow}
				onNavigation={navigateHandler}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		],
		[
			{ columnSpan: 2, rowSpan: 2 },
			() => (
				<TwoByTwoTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
VilansKickTileContent.displayName = "VilansKickTileContent";

export function useVilansKickTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.VilansKick>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);

	const {
		environmentAppId,
		settings: { destinationWindow }
	} = renderStateIsh.data;

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useCallback(
			(): void => {
				registerTileUsage();

				navigateToVilansKickApp({
					id: environmentAppId,
					destinationWindow
				});
			},
			[
				destinationWindow,
				environmentAppId,
				registerTileUsage
			]
		)
	);
}
